// @ts-ignore
import store from '@/store';
// @ts-ignore
import { createPlaceholderComponent } from './placeholder';

type ValidConsentState = 'implied' | 'viewed' | 'accepted' | 'declined';

type ValidConsentAction = 'implied' | 'user_click' | 'import';

export type ValidCookieSubcategory =
  | 'analytics'
  | 'functional'
  | 'marketing'
  | 'unclassified';

type ConsentEvent = {
  category: string;
  subcategory: string;
  label: string;
  consentState: ValidConsentState;
  consentAction: ValidConsentAction;
};

export function coverBlockedIframesAndImages() {
  // @ts-ignore
  if (!store.config?.regionTemplate?.settings?.placeholders?.enabled) return;
  iframeHelper();
  imageHelper();
}

const iframeHelper = () => {
  const processIframes = () => {
    const iframes = document.querySelectorAll('iframe');

    iframes.forEach((iframe) => {
      // we only need to process iFrames that are blocked
      if (iframe.getAttribute('data-concord-modified') !== 'blocked') return;

      const alreadyCovered: boolean =
        iframe.previousElementSibling?.classList.contains('placeholder') ??
        false;

      iframe.style.setProperty(
        'display',
        iframe.getAttribute('data-concord-display') ?? 'block',
      );
      const rect = iframe.getBoundingClientRect();

      const displayHeight = rect.height;
      const displayWidth = rect.width;

      iframe.style.setProperty('display', 'none');

      if (!alreadyCovered && displayHeight > 0 && displayWidth > 0) {
        const displayStyle =
          iframe.getAttribute('data-concord-display') ?? 'block';

        const placeholder = createPlaceholderComponent({
          // @ts-ignore
          acceptButtonText: store.config?.language['placeholders.acceptButton'],
          // @ts-ignore
          acceptText: store.config?.language['placeholders.acceptText'],
          analyticsText:
            // @ts-ignore
            store.config?.language[`privacyCenter.consentCategory.analytics`],
          functionalText:
            // @ts-ignore
            store.config?.language[`privacyCenter.consentCategory.functional`],
          marketingText:
            // @ts-ignore
            store.config?.language[`privacyCenter.consentCategory.marketing`],
          unclassifiedText:
            // @ts-ignore
            store.config?.language[
              `privacyCenter.consentCategory.unclassified`
            ],
          blockedElement: iframe,
          // @ts-ignore
          buttonRadius:
            store.config?.regionTemplate?.settings?.branding?.buttonRadius,
          // @ts-ignore
          cardRadius:
            store.config?.regionTemplate?.settings?.branding?.cardRadius,
          // @ts-ignore
          primaryColor:
            store.config?.regionTemplate?.settings?.branding?.primaryColor,
          // @ts-ignore
          secondaryColor:
            store.config?.regionTemplate?.settings?.branding?.secondaryColor,
          displayStyle: displayStyle,
          height: displayHeight,
          width: displayWidth,
          theme:
            // @ts-ignore
            store.config?.regionTemplate?.settings?.placeholders?.theme,
          onclick: () => {
            const contentType = iframe.getAttribute('data-concord-category');
            if (contentType === undefined) {
              throw Error(
                'Consent type not recognized upon placeholder creation',
              );
            }
            unblockContent(new Set([contentType as ValidCookieSubcategory]));
          },
        });

        iframe.parentNode?.insertBefore(placeholder, iframe);

        /* Observer logic will remove placeholder if iFrame ever redisplayed */
        const observerOptions = {
          attributes: true,
          attributeFilter: ['style'],
        };

        const observerCallback = (
          mutationsList: MutationRecord[],
          observer: MutationObserver,
        ) => {
          mutationsList.forEach((mutation) => {
            // Check if the 'display' property was changed
            if (
              mutation.type === 'attributes' &&
              mutation.attributeName === 'style'
            ) {
              const displayStyle = iframe.style.display;

              if (displayStyle !== 'none' && placeholder) {
                placeholder.remove();
                observer.disconnect();
              }
            }
          });
        };

        const observer = new MutationObserver(observerCallback);
        observer.observe(iframe, observerOptions);
      }
    });
  };

  // Call processIframes immediately when this helper is initialized
  processIframes();

  // Then set up the event listener for when the page fully loads
  window.addEventListener('load', processIframes);
};

const imageHelper = () => {
  const images = document.querySelectorAll('img');

  images.forEach((image) => {
    // we only need to process images that are blocked
    if (image.getAttribute('data-concord-modified') !== 'blocked') return;

    const alreadyCovered: boolean =
      image.previousElementSibling?.classList.contains('placeholder') ?? false;

    if (alreadyCovered) return;

    // marker prevents race condition caused by async nature of onload event
    const currentlyProcessingMarker = document.createElement('div');
    currentlyProcessingMarker.classList.add('placeholder');
    image.parentNode?.insertBefore(currentlyProcessingMarker, image);

    const pseudoElem = document.createElement('img');
    const imageUrl = image.getAttribute('data-concord-src') ?? '';
    pseudoElem.src = imageUrl;

    // object creation here is asynchronous
    pseudoElem.onload = () => {
      const displayHeight = pseudoElem.naturalHeight;
      const displayWidth = pseudoElem.naturalWidth;

      if (displayHeight > 0 && displayWidth > 0) {
        const displayStyle =
          image.getAttribute('data-concord-display') ?? 'inline';

        const placeholder = createPlaceholderComponent({
          blockedElement: image,
          // @ts-ignore
          buttonRadius:
            store.config?.regionTemplate?.settings?.branding?.buttonRadius,
          // @ts-ignore
          cardRadius:
            store.config?.regionTemplate?.settings?.branding?.cardRadius,
          // @ts-ignore
          primaryColor:
            store.config?.regionTemplate?.settings?.branding?.primaryColor,
          // @ts-ignore
          secondaryColor:
            store.config?.regionTemplate?.settings?.branding?.secondaryColor,
          displayStyle: displayStyle,
          height: displayHeight,
          width: displayWidth,
          theme:
            // @ts-ignore
            store.config?.regionTemplate?.settings?.placeholders?.theme,
          onclick: () => {
            const contentType = image.getAttribute('data-concord-category');
            if (contentType === undefined) {
              throw Error(
                'Consent type not recognized on placeholder creation',
              );
            }
            unblockContent(new Set([contentType as ValidCookieSubcategory]));
          },
        });

        image.parentNode?.insertBefore(placeholder, image);

        /* Observer logic will remove placeholder if image ever redisplayed */
        const observerOptions = {
          attributes: true,
          attributeFilter: ['style'],
        };

        const observerCallback = (
          mutationsList: MutationRecord[],
          observer: MutationObserver,
        ) => {
          mutationsList.forEach((mutation) => {
            // Check if the 'display' property was changed
            if (
              mutation.type === 'attributes' &&
              mutation.attributeName === 'style'
            ) {
              const displayStyle = image.style.display;

              if (displayStyle !== 'none' && placeholder) {
                placeholder.remove();
                observer.disconnect();
              }
            }
          });
        };

        const observer = new MutationObserver(observerCallback);
        observer.observe(image, observerOptions);
      }

      currentlyProcessingMarker.remove();
    };
  });
};

const generateConsentEventsArr = (
  subcategoriesToEnable: Set<ValidCookieSubcategory>,
): ConsentEvent[] => {
  const consentEventsArr: ConsentEvent[] = [];

  // Create events based on allowState structure
  const allowState = Object.entries(
    // @ts-ignore
    store.consentState?.allowState || {},
  );

  // @ts-ignore
  allowState.forEach(([subcategory, isAccepted]) => {
    // Only include valid cookie subcategories
    if (
      ['analytics', 'functional', 'marketing', 'unclassified'].includes(
        subcategory,
      )
    ) {
      consentEventsArr.push({
        category: 'privacy_setting', // Default category for all consent settings
        subcategory: subcategory,
        label: 'consent', // Default label for all consent settings
        consentState: subcategoriesToEnable.has(
          subcategory as ValidCookieSubcategory,
        )
          ? 'accepted'
          : isAccepted
            ? 'accepted'
            : 'declined',
        consentAction: 'user_click', // Default action for all consent settings
      });
    }
  });

  return consentEventsArr;
};

const unblockContent = (
  subcategoriesToEnable: Set<ValidCookieSubcategory>,
): void => {
  const arr: ConsentEvent[] = generateConsentEventsArr(subcategoriesToEnable);
  // @ts-ignore
  window.concord.consent.addConsentEvents(arr);
};
